import { FormHelperText, Stack, Typography } from '@mui/material';
import { type ChangeEvent, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DynamicIcon } from 'src/components';
import { FormSwitch } from 'src/components/Forms';
import {
  billingOptionFormControlSx,
  BillingOptionsList,
  billingOptionTitleSx,
  planToggleLabelSx,
  SectionAccordion,
  SectionAccordionDetails,
  SectionAccordionSummary,
  SectionSummaryContent,
  SectionSummaryTitle,
  SectionWrapper,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  type PlansTabBillingOptionsValues,
  PlansTabField,
  PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

const BILLING_OPTION_VALIDATION_MESSAGE = 'At least one billing option must remain enabled.';

type BillingToggleConfig = {
  field: PlansTabField.BillingMonthlyEnabled | PlansTabField.BillingAnnualEnabled;
  label: string;
  disabled: boolean;
};

const BILLING_OPTION_KEYS = {
  [PlansTabField.BillingMonthlyEnabled]: 'monthlyEnabled',
  [PlansTabField.BillingAnnualEnabled]: 'annualEnabled',
} as const satisfies Record<
  PlansTabField.BillingMonthlyEnabled | PlansTabField.BillingAnnualEnabled,
  keyof PlansTabBillingOptionsValues
>;

const getBillingOptionKey = (
  field: PlansTabField.BillingMonthlyEnabled | PlansTabField.BillingAnnualEnabled,
): keyof PlansTabBillingOptionsValues => BILLING_OPTION_KEYS[field];

type BillingOptionsProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  readOnly: boolean;
};

export const BillingOptions = ({
  expanded,
  onExpandedChange,
  readOnly,
}: BillingOptionsProps) => {
  const {
    clearErrors,
    formState: { errors },
    control,
  } = useFormContext<PlansTabFormValues>();

  const billingOptions = useWatch({
    control,
    name: PlansTabField.BillingOptions,
  }) as PlansTabBillingOptionsValues | undefined;

  const monthlyEnabled = billingOptions?.monthlyEnabled ?? false;
  const annualEnabled = billingOptions?.annualEnabled ?? false;

  const billingToggleConfigs: BillingToggleConfig[] = useMemo(() => {
    return [
      {
        field: PlansTabField.BillingMonthlyEnabled,
        label: 'Monthly billing',
        disabled: !annualEnabled,
      },
      {
        field: PlansTabField.BillingAnnualEnabled,
        label: 'Annual billing',
        disabled: !monthlyEnabled,
      },
    ];
  }, [annualEnabled, monthlyEnabled]);

  const monthlyError = errors.billingOptions?.monthlyEnabled?.message;
  const annualError = errors.billingOptions?.annualEnabled?.message;
  const errorMessage = monthlyError || annualError;

  const handleToggleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      clearErrors([PlansTabField.BillingMonthlyEnabled, PlansTabField.BillingAnnualEnabled]);
    }
  };

  return (
    <SectionWrapper data-testid="billing-options">
      <SectionAccordion
        elevation={0}
        expanded={expanded}
        TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
        onChange={(_, expandedValue) => {
          onExpandedChange(expandedValue);
        }}
        disableGutters
      >
        <SectionAccordionSummary
          expandIcon={<DynamicIcon name="chevron-right" library="lucide" />}
        >
          <SectionSummaryContent>
            <SectionSummaryTitle variant="h6">Billing options</SectionSummaryTitle>
          </SectionSummaryContent>
        </SectionAccordionSummary>

        <SectionAccordionDetails>
          <BillingOptionsList>
            {billingToggleConfigs.map(({ field, label, disabled }) => {
              const dependentField =
                field === PlansTabField.BillingMonthlyEnabled
                  ? PlansTabField.BillingAnnualEnabled
                  : PlansTabField.BillingMonthlyEnabled;

              return (
                <FormSwitch
                  key={field}
                  type="switch"
                  name={field}
                  label={<Typography sx={billingOptionTitleSx}>{label}</Typography>}
                  labelPlacement="start"
                  sx={billingOptionFormControlSx}
                  labelSx={planToggleLabelSx}
                  switchFieldProps={{
                    size: 'small',
                    inputProps: { 'aria-label': label },
                  }}
                  disabled={readOnly || disabled}
                  rules={{
                    deps: [dependentField],
                    validate: (value, formValues) => {
                      if (value) {
                        return true;
                      }

                      const dependentKey = getBillingOptionKey(dependentField);
                      const typedFormValues = formValues as PlansTabFormValues;

                      if (typedFormValues.billingOptions[dependentKey]) {
                        return true;
                      }

                      return BILLING_OPTION_VALIDATION_MESSAGE;
                    },
                  }}
                  onChange={handleToggleChange}
                />
              );
            })}
          </BillingOptionsList>

          {errorMessage ? (
            <Stack mt={1}>
              <FormHelperText error>{errorMessage}</FormHelperText>
            </Stack>
          ) : null}
        </SectionAccordionDetails>
      </SectionAccordion>
    </SectionWrapper>
  );
};
